"use client";

import type { CTAGroupItem } from "@/components/CTAGroup/types";
import type { LocalizationServiceabilityProps } from "@/components/LocalizationServiceability";
import { useAppContext } from "@/context/AppContext";
import type { HeroOverlayType } from "@/types/monarch";
import HeroOverlay from ".";
import { defaultHeroOverlayData } from "./default";

interface BaseHeroOverlayProps {
  data?: HeroOverlayType;
}

const BaseHeroOverlay = ({ data }: BaseHeroOverlayProps) => {
  const monarchData = data ?? defaultHeroOverlayData;
  const { localizationType } = useAppContext();

  const {
    title,
    eyebrow,
    bulletPoints,
    description,
    pricing,
    primaryCtaLeft,
    secondaryCtaLeft,
    primaryCtaRight,
    secondaryCtaRight,
    valueProps,
    legal,
    backgroundImages,
    localizationServiceability,
  } = monarchData;

  const descriptionProp =
    typeof description === "string"
      ? { copy: description }
      : description
        ? {
            copy: description.copy,
            alignmentObject: description.alignment,
          }
        : undefined;
  const mobileImageTreatment: "lifestyle" | "background" =
    backgroundImages?.mobileImageTreatment === "background"
      ? "background"
      : "lifestyle";

  const desktopBackgroundImage = backgroundImages?.desktop?.trim() ?? "";
  const tabletBackgroundImage =
    backgroundImages?.tablet?.trim() || desktopBackgroundImage;
  const mobileBackgroundImage =
    backgroundImages?.mobile?.trim() || desktopBackgroundImage;

  const backgroundImagesObject = {
    desktop: desktopBackgroundImage,
    tablet: tabletBackgroundImage,
    mobile: mobileBackgroundImage,
    alt: backgroundImages?.alt?.trim() ?? "Hero background",
    mobileImageTreatment,
  };
  const shouldRenderLocalizationServiceability =
    localizationServiceability?.isEnabled === true;
  const localizationServiceabilityElementName =
    localizationServiceability?.elementName?.trim() || "HeroOverlay";
  const localizationServiceabilityDestinationUrl =
    localizationServiceability?.hasCustomDestinationUrl
      ? localizationServiceability?.destinationUrl?.trim() || undefined
      : undefined;
  const normalizedLocalizationSecondaryCta =
    localizationServiceability?.secondaryCta
      ? {
          ...localizationServiceability.secondaryCta,
          copy: localizationServiceability.secondaryCta.copy?.trim() || "",
          url: localizationServiceability.secondaryCta.url?.trim(),
          modalKey: localizationServiceability.secondaryCta.modalKey?.trim(),
        }
      : undefined;
  const localizationSecondaryCtaItems: CTAGroupItem[] =
    normalizedLocalizationSecondaryCta?.copy
      ? [normalizedLocalizationSecondaryCta].map((cta): CTAGroupItem => {
          const isTextLink = cta.display === "ghost";
          const display: CTAGroupItem["display"] = isTextLink
            ? "textLink"
            : cta.display;

          return {
            copy: cta.copy,
            action: cta.action,
            url: cta.url,
            modalKey: cta.modalKey,
            display,
            tone: cta.tone,
            size: cta.size,
            icon: cta.icon,
            className: isTextLink
              ? "w-full cursor-pointer text-center text-[16px] leading-5.5 font-normal decoration-solid md:w-auto md:text-left"
              : undefined,
            tagularProps: {
              name:
                cta.elementName ??
                (cta.action === "modal"
                  ? (cta.modalKey ?? "textLinkModal")
                  : "HeroOverlay"),
            },
          };
        })
      : [];
  const localizationServiceabilityField = {
    inputId:
      localizationServiceability?.field?.inputId?.trim() ||
      "hero-overlay-serviceability-address-input",
    label: localizationServiceability?.field?.label?.trim(),
    placeholder:
      localizationServiceability?.field?.placeholder?.trim() ||
      "Enter your address",
    ariaLabel: localizationServiceability?.field?.ariaLabel?.trim(),
    autoComplete:
      localizationServiceability?.field?.autoComplete?.trim() ||
      "street-address",
    fieldName:
      localizationServiceability?.field?.fieldName?.trim() ||
      "serviceabilityAddress",
    blurFieldLabel:
      localizationServiceability?.field?.blurFieldLabel?.trim() ||
      localizationServiceability?.field?.label?.trim() ||
      localizationServiceability?.field?.placeholder?.trim() ||
      "Enter your address",
    selectFieldLabel:
      localizationServiceability?.field?.selectFieldLabel?.trim() ||
      "Address selection",
  };
  const localizationServiceabilityPrimaryCta = {
    copy:
      localizationServiceability?.primaryCta?.copy?.trim() ||
      "Check availability",
    loadingCopy:
      localizationServiceability?.primaryCta?.loadingCopy?.trim() ||
      "Checking availability...",
    display: localizationServiceability?.primaryCta?.display ?? "fill",
    tone: localizationServiceability?.primaryCta?.tone ?? "neutral-dark",
    size: localizationServiceability?.primaryCta?.size ?? "md",
    className: localizationServiceability?.primaryCta?.className,
  };
  const localizationServiceabilitySecondaryCtaGroup =
    localizationSecondaryCtaItems.length > 0
      ? {
          ctas: localizationSecondaryCtaItems,
          location: "hero" as const,
          localizationType,
          defaultDisplay: "fill" as const,
          defaultTone: "neutral-dark" as const,
          defaultSize: "md" as const,
          orientation: {
            mobile: "vertical" as const,
            tablet: "horizontal" as const,
            xl: "horizontal" as const,
          },
          alignment: {
            mobile: "center" as const,
            tablet: "left" as const,
            xl: "left" as const,
          },
          className: "gap-4 md:gap-6",
        }
      : undefined;
  const localizationServiceabilityProps:
    | LocalizationServiceabilityProps
    | undefined = shouldRenderLocalizationServiceability
    ? {
        field: localizationServiceabilityField,
        primaryCta: localizationServiceabilityPrimaryCta,
        secondaryCtaGroup: localizationServiceabilitySecondaryCtaGroup,
        statusCopy: localizationServiceability?.statusCopy,
        showSelectedAddress:
          localizationServiceability?.showSelectedAddress ?? true,
        statusPlacement: localizationServiceability?.statusPlacement ?? "below",
        ctaPlacement: localizationServiceability?.ctaPlacement ?? "inline",
        secondaryCtaTabletPlacement:
          localizationServiceability?.secondaryCtaTabletPlacement ?? "inline",
        inputContainerClassName: "gap-4",
        rowClassName: "gap-4",
        inputWrapperClassName: "w-full",
        ctaContainerClassName: "items-stretch md:items-center",
        submitTracking: {
          actionOutcomeWhenLocalized: "sentToOffers",
          actionOutcomeWhenNotLocalized: "sentToLocalize",
          elementType: "ctaButton",
          location: "hero",
          name: localizationServiceabilityElementName,
          text: localizationServiceability?.primaryCta?.copy?.trim(),
        },
        inputEngagedTracking: {
          elementType: "textInput",
          location: "hero",
          text: "Address input focused",
          name: `Field Engaged${localizationServiceabilityElementName}`,
        },
        suggestionsTestId: "hero-overlay-serviceability-suggestions",
        destinationUrl: localizationServiceabilityDestinationUrl,
      }
    : undefined;

  return (
    <HeroOverlay
      title={title}
      eyebrow={eyebrow}
      bulletPoints={bulletPoints}
      description={descriptionProp}
      pricing={pricing}
      primaryCtaLeft={primaryCtaLeft}
      secondaryCtaLeft={secondaryCtaLeft}
      primaryCtaRight={primaryCtaRight}
      secondaryCtaRight={secondaryCtaRight}
      backgroundImages={backgroundImagesObject}
      valueProps={valueProps}
      legal={legal}
      localizationServiceability={localizationServiceabilityProps}
    />
  );
};

export default BaseHeroOverlay;
